import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const authHeaders = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

const INDIA_CENTER = [22.5, 79];
const DEFAULT_RADIUS_KM = 15;
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const TYPE_LABEL = {
  mechanic: 'Mechanic',
  towing: 'Towing',
  both: 'Mech + Towing',
};

const SPEC_FILTERS = [
  { id: 'all', label: 'All specs' },
  { id: 'Jump Start', label: 'Jump Start' },
  { id: 'Towing', label: 'Towing' },
  { id: 'Flat Tyre', label: 'Flat Tyre' },
  { id: 'Fuel Delivery', label: 'Fuel Delivery' },
  { id: 'Key Lockout', label: 'Key Lockout' },
  { id: 'Minor Repair', label: 'Minor Repair' },
];

const partnerSpecText = (row) =>
  [row.service_tag, row.service_tag2, row.service_tag3].filter(Boolean).join(', ');

const normalizeSpecs = (raw) => {
  const out = [];
  String(raw || '')
    .split(/[,·|/]/)
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((tag) => {
      if (SPEC_FILTERS.some((s) => s.id === tag) && !out.includes(tag)) {
        out.push(tag);
        return;
      }
      const lower = tag.toLowerCase();
      let mapped = null;
      if (lower.includes('fuel') || lower.includes('petrol') || lower.includes('diesel')) {
        mapped = 'Fuel Delivery';
      } else if (lower.includes('jump') || lower.includes('battery')) {
        mapped = 'Jump Start';
      } else if (
        lower.includes('punct') ||
        lower.includes('flat') ||
        lower.includes('tyre') ||
        lower.includes('tire')
      ) {
        mapped = 'Flat Tyre';
      } else if (lower.includes('tow')) {
        mapped = 'Towing';
      } else if (lower.includes('lock') || lower.includes('key')) {
        mapped = 'Key Lockout';
      } else if (lower.includes('minor') || lower.includes('repair') || lower.includes('break')) {
        mapped = 'Minor Repair';
      }
      if (mapped && !out.includes(mapped)) out.push(mapped);
    });
  return out;
};

const matchesSpec = (row, specId) => {
  if (!specId || specId === 'all') return true;
  return normalizeSpecs(partnerSpecText(row)).includes(specId);
};

const parseLatLng = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return [a, b];
};

const partnerName = (row) =>
  String(row.shop_name || row.mechanic_name || row.code || 'Partner').trim() || 'Partner';

const crewName = (row) =>
  String(row.display_name || row.phone || 'Staff').trim() || 'Staff';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  return `${a.toFixed(5)}, ${b.toFixed(5)}`;
};

const parseHexPoints = (raw) => {
  if (!Array.isArray(raw) || raw.length < 3) return null;
  const pts = raw.map((item) => parseLatLng(item?.lat ?? item?.latitude, item?.lng ?? item?.longitude));
  if (pts.some((p) => !p)) return null;
  return pts;
};

const loadLeaflet = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__roadserveLeafletPromise) return window.__roadserveLeafletPromise;

  window.__roadserveLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => {
      if (window.L) resolve(window.L);
      else reject(new Error('Leaflet failed to load'));
    };
    script.onerror = () => reject(new Error('Leaflet script blocked'));
    document.body.appendChild(script);
  });
  return window.__roadserveLeafletPromise;
};

const fetchAllWithCoords = async () => {
  const rows = [];
  let offset = 0;
  const limit = 1000;
  while (true) {
    const params = new URLSearchParams();
    params.set(
      'select',
      'id,code,mechanic_name,shop_name,number,city,area,partner_type,active,owner_user_id,kyc_status,service_tag,service_tag2,service_tag3,service_center_lat,service_center_lng,service_radius_km,service_area_hex',
    );
    params.set('service_center_lat', 'not.is.null');
    params.set('service_center_lng', 'not.is.null');
    params.set('order', 'mechanic_name.asc.nullslast');
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    const res = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?${params}`, { headers: authHeaders });
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) break;
    rows.push(...data);
    if (data.length < limit) break;
    offset += limit;
  }
  return rows.filter((row) => parseLatLng(row.service_center_lat, row.service_center_lng));
};

const countLinkedMissingGps = async () => {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/mechanics?select=id&owner_user_id=not.is.null&or=(service_center_lat.is.null,service_center_lng.is.null)&limit=1`,
      { headers: { ...authHeaders, Prefer: 'count=exact' } },
    );
    const header = res.headers.get('content-range') || '';
    const m = header.match(/\/(\d+)$/);
    return m ? Number(m[1]) || 0 : 0;
  } catch {
    return 0;
  }
};

const JOB_SELECT =
  'id, pickup_latitude, pickup_longitude, pickup_address, location_link, created_at, request_status, service_type, assigned_mechanic_id, assigned_mechanic_name, customer_name, vehicle_number';

const fetchJobsByIds = async (ids) => {
  const jobsById = new Map();
  const unique = [...new Set(ids.map((id) => String(id || '')).filter(Boolean))];
  for (let i = 0; i < unique.length; i += 40) {
    const chunk = unique.slice(i, i + 40);
    const { data, error } = await serviceRoleClient
      .from('service_requests')
      .select(JOB_SELECT)
      .in('id', chunk);
    if (error) continue;
    (data || []).forEach((job) => jobsById.set(String(job.id), job));
  }
  return jobsById;
};

const fetchRecentJobs = async () => {
  try {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await serviceRoleClient
      .from('service_requests')
      .select(JOB_SELECT)
      .not('pickup_latitude', 'is', null)
      .not('pickup_longitude', 'is', null)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(400);
    if (error || !Array.isArray(data)) return [];
    return data.filter((row) => parseLatLng(row.pickup_latitude, row.pickup_longitude));
  } catch {
    return [];
  }
};

const normalizeRingRow = (row) => {
  const job = row.job && typeof row.job === 'object' ? row.job : null;
  const pickupAddress =
    row.pickup_address ||
    job?.pickup_address ||
    job?.location_link ||
    '';
  return {
    request_id: row.request_id,
    bell_started_at: row.ring_at || row.bell_started_at,
    bell_seconds: row.bell_seconds,
    job: job || (row.job_found === false ? null : {
      id: row.request_id,
      service_type: row.service_type,
      pickup_address: pickupAddress,
      pickup_latitude: row.pickup_lat ?? row.pickup_latitude,
      pickup_longitude: row.pickup_lng ?? row.pickup_longitude,
      request_status: row.status || row.request_status,
      customer_name: row.customer_name,
      vehicle_number: row.vehicle_number,
      assigned_mechanic_id: row.assigned_mechanic_id,
      assigned_mechanic_name: row.assigned_mechanic_name,
    }),
  };
};

const fetchRecentRings = async () => {
  const byMech = new Map();
  const push = (mechanicId, item) => {
    const id = String(mechanicId || '');
    if (!id) return;
    const list = byMech.get(id) || [];
    list.push(item);
    byMech.set(id, list);
  };

  try {
    const rpc = await serviceRoleClient.rpc('admin_coverage_rings', { p_days: 14 });
    const rows = Array.isArray(rpc.data)
      ? rpc.data
      : Array.isArray(rpc.data?.rings)
        ? rpc.data.rings
        : [];
    if (!rpc.error && rows.length) {
      rows.forEach((row) => {
        const item = normalizeRingRow(row);
        if (!item.job?.service_type && !item.job?.pickup_address && row.job_found === false) {
          item.job = null;
        }
        push(row.mechanic_id, item);
      });
      return byMech;
    }
  } catch {
    /* RPC not applied yet */
  }

  try {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const embedded = await serviceRoleClient
      .from('partner_job_bell_targets')
      .select(
        'mechanic_id, request_id, bell_started_at, bell_seconds, job:service_requests(id, pickup_address, pickup_latitude, pickup_longitude, location_link, service_type, request_status, assigned_mechanic_id, assigned_mechanic_name, customer_name, vehicle_number)',
      )
      .gte('bell_started_at', since)
      .order('bell_started_at', { ascending: false })
      .limit(4000);

    if (!embedded.error && Array.isArray(embedded.data) && embedded.data.length) {
      const missing = embedded.data.filter((row) => !row.job).map((row) => row.request_id);
      const extras = missing.length ? await fetchJobsByIds(missing) : new Map();
      embedded.data.forEach((row) => {
        const job = row.job || extras.get(String(row.request_id)) || null;
        push(row.mechanic_id, {
          request_id: row.request_id,
          bell_started_at: row.bell_started_at,
          bell_seconds: row.bell_seconds,
          job,
        });
      });
      return byMech;
    }

    const { data, error } = await serviceRoleClient
      .from('partner_job_bell_targets')
      .select('mechanic_id, request_id, bell_started_at, bell_seconds')
      .gte('bell_started_at', since)
      .order('bell_started_at', { ascending: false })
      .limit(4000);
    if (error || !Array.isArray(data) || !data.length) return byMech;

    const jobsById = await fetchJobsByIds(data.map((row) => row.request_id));
    data.forEach((row) => {
      push(row.mechanic_id, {
        request_id: row.request_id,
        bell_started_at: row.bell_started_at,
        bell_seconds: row.bell_seconds,
        job: jobsById.get(String(row.request_id)) || null,
      });
    });
  } catch {
    /* table may be missing / RLS */
  }
  return byMech;
};

const formatRingWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  const clock = d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  if (mins < 1) return `Just now · ${clock}`;
  if (mins < 60) return `${mins} min ago · ${clock}`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} hr ago · ${clock}`;
  return clock;
};

const jobStatusLabel = (job) => {
  const raw = String(job?.request_status || job?.complaint_status || '')
    .toLowerCase()
    .replace(/_/g, ' ');
  if (!raw) return '';
  if (raw.includes('complete')) return 'Completed';
  if (raw.includes('cancel')) return 'Cancelled';
  if (raw.includes('assign') || raw.includes('on the way') || raw.includes('accepted')) {
    return 'Assigned';
  }
  if (raw.includes('quote') || raw.includes('pending') || raw.includes('open')) {
    return 'Waiting for quote';
  }
  return raw;
};

const ringOutcome = (item, partnerId) => {
  const job = item.job;
  if (!job) return 'Notification sent. Job record not found.';
  const assignedId = job.assigned_mechanic_id ? String(job.assigned_mechanic_id) : '';
  if (assignedId && assignedId === String(partnerId)) {
    return 'Assigned to this shop';
  }
  if (assignedId || job.assigned_mechanic_name) {
    return `Assigned to another partner${job.assigned_mechanic_name ? ` (${job.assigned_mechanic_name})` : ''}`;
  }
  return 'Notified — not assigned yet';
};

const pickupText = (job) =>
  String(job?.pickup_address || job?.location_link || '').trim();

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const a = parseLatLng(lat1, lng1);
  const b = parseLatLng(lat2, lng2);
  if (!a || !b) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)) * 10) / 10;
};

const ringListHtml = (rings, partnerId, shopLat, shopLng) => {
  if (!rings.length) return '';
  const rows = rings
    .slice(0, 10)
    .map((item, idx) => {
      const job = item.job;
      const svc = String(job?.service_type || '').trim() || 'Service not recorded';
      const addr = pickupText(job) || 'No pickup address on this job';
      const km = haversineKm(shopLat, shopLng, job?.pickup_latitude, job?.pickup_longitude);
      const canFly = parseLatLng(job?.pickup_latitude, job?.pickup_longitude);
      const extra = [job?.customer_name, job?.vehicle_number].filter(Boolean).join(' · ');
      return `<div style="padding:8px 0;border-top:1px solid #e2e8f0">
        <div style="font-size:10px;color:#64748b;font-weight:700">${idx + 1}. Job ring</div>
        <div style="font-weight:800;font-size:12px;color:#0f172a">${escapeHtml(svc)}</div>
        <div style="font-size:11px;color:#334155;margin-top:2px"><b>Pickup:</b> ${escapeHtml(addr)}</div>
        <div style="font-size:11px;color:#334155"><b>When:</b> ${escapeHtml(formatRingWhen(item.bell_started_at))}</div>
        <div style="font-size:11px;color:#334155"><b>Result:</b> ${escapeHtml(ringOutcome(item, partnerId))}</div>
        ${
          km != null
            ? `<div style="font-size:11px;color:#334155"><b>Distance from shop:</b> ${km} km</div>`
            : ''
        }
        ${jobStatusLabel(job) ? `<div style="font-size:11px;color:#64748b">Status: ${escapeHtml(jobStatusLabel(job))}</div>` : ''}
        ${extra ? `<div style="font-size:10px;color:#64748b">${escapeHtml(extra)}</div>` : ''}
        ${
          canFly
            ? `<a href="#" data-fly-lat="${job.pickup_latitude}" data-fly-lng="${job.pickup_longitude}" style="display:inline-block;margin-top:4px;font-size:11px;color:#4f46e5;font-weight:800">Show pickup on map</a>`
            : ''
        }
      </div>`;
    })
    .join('');
  return `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #c7d2fe">
    <div style="color:#4f46e5;font-weight:800;font-size:12px">${rings.length} job rings (last 14 days)</div>
    <div style="font-size:10px;color:#64748b;margin:2px 0 4px">Job notifications sent to this partner’s app.</div>
    ${rows}
    ${
      rings.length > 10
        ? `<div style="font-size:11px;color:#64748b">+${rings.length - 10} more rings</div>`
        : ''
    }
  </div>`;
};

const fetchCrewMembers = async () => {
  try {
    const { data, error } = await serviceRoleClient
      .from('partner_crew_members')
      .select(
        'id, partner_id, display_name, phone, status, service_center_lat, service_center_lng, service_radius_km, service_area_hex, can_set_service_area, specialization',
      )
      .eq('status', 'active')
      .limit(4000);
    if (error || !Array.isArray(data)) return [];
    return data;
  } catch {
    return [];
  }
};

const styleFor = (row, ringCount) => {
  const linked = Boolean(row.owner_user_id);
  const active = row.active === true;
  if (linked && active) {
    return {
      color: ringCount > 0 ? '#4f46e5' : '#059669',
      fill: ringCount > 0 ? '#6366f1' : '#10b981',
      label: ringCount > 0 ? 'Linked · rings going' : 'Linked · can receive rings',
    };
  }
  if (linked) {
    return { color: '#d97706', fill: '#f59e0b', label: 'Linked · inactive' };
  }
  return { color: '#64748b', fill: '#94a3b8', label: 'Not linked · no app login' };
};

const ensurePanes = (map, L) => {
  if (!map.getPane('coverageArea')) {
    map.createPane('coverageArea');
    map.getPane('coverageArea').style.zIndex = 410;
  }
  if (!map.getPane('coveragePins')) {
    map.createPane('coveragePins');
    map.getPane('coveragePins').style.zIndex = 650;
    map.getPane('coveragePins').style.pointerEvents = 'auto';
  }
  if (!map.getPane('coverageStaff')) {
    map.createPane('coverageStaff');
    map.getPane('coverageStaff').style.zIndex = 660;
    map.getPane('coverageStaff').style.pointerEvents = 'auto';
  }
  if (!map.getPane('coverageJobs')) {
    map.createPane('coverageJobs');
    map.getPane('coverageJobs').style.zIndex = 680;
    map.getPane('coverageJobs').style.pointerEvents = 'auto';
  }
  void L;
};

const bindHoverPopup = (layer, html, { onOpen } = {}) => {
  layer.bindPopup(html, { maxWidth: 340, maxHeight: 380, autoPanPadding: [24, 24], closeButton: true });
  let closeT = null;
  const cancel = () => {
    if (closeT) {
      clearTimeout(closeT);
      closeT = null;
    }
  };
  layer.on('mouseover', () => {
    cancel();
    layer.openPopup();
    if (typeof layer.bringToFront === 'function') layer.bringToFront();
  });
  layer.on('mouseout', () => {
    cancel();
    closeT = setTimeout(() => layer.closePopup(), 500);
  });
  layer.on('click', () => {
    cancel();
    layer.openPopup();
  });
  layer.on('popupopen', (e) => {
    const el = e.popup.getElement();
    if (!el) return;
    el.addEventListener('mouseenter', cancel);
    el.addEventListener('mouseleave', () => {
      cancel();
      closeT = setTimeout(() => layer.closePopup(), 400);
    });
    onOpen?.(el);
  });
};

const drawSavedArea = (L, group, latlng, row, style) => {
  const hex = parseHexPoints(row.service_area_hex);
  const km = Number(row.service_radius_km) > 0 ? Number(row.service_radius_km) : DEFAULT_RADIUS_KM;
  if (hex) {
    L.polygon(hex, {
      pane: 'coverageArea',
      color: style.color,
      weight: 1.4,
      fillColor: style.fill,
      fillOpacity: 0.1,
      interactive: false,
    }).addTo(group);
    return { kind: 'hexagon', km };
  }
  L.circle(latlng, {
    pane: 'coverageArea',
    radius: km * 1000,
    color: style.color,
    weight: 1.2,
    fillColor: style.fill,
    fillOpacity: 0.1,
    interactive: false,
  }).addTo(group);
  return { kind: 'circle', km };
};

const PartnerCoverageMap = ({ open, onClose }) => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef(new Map());
  const partnersLayerRef = useRef(null);
  const staffLayerRef = useRef(null);
  const jobsLayerRef = useRef(null);
  const drawGenRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [ringsByMechanic, setRingsByMechanic] = useState(new Map());
  const [crew, setCrew] = useState([]);
  const [missingGps, setMissingGps] = useState(0);
  const [filterActive, setFilterActive] = useState('active');
  const [filterLinked, setFilterLinked] = useState('linked');
  const [filterKyc, setFilterKyc] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [specFilter, setSpecFilter] = useState('all');
  const [showJobs, setShowJobs] = useState(true);
  const [showStaff, setShowStaff] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partners.filter((row) => {
      const linked = Boolean(row.owner_user_id);
      const active = row.active === true;
      const kycVerified = String(row.kyc_status || '').toLowerCase() === 'verified';
      if (filterActive === 'active' && !active) return false;
      if (filterActive === 'inactive' && active) return false;
      if (filterLinked === 'linked' && !linked) return false;
      if (filterLinked === 'unlinked' && linked) return false;
      if (filterKyc === 'verified' && !kycVerified) return false;
      if (filterKyc === 'unverified' && kycVerified) return false;
      if (typeFilter !== 'all' && String(row.partner_type || 'mechanic') !== typeFilter) return false;
      if (!matchesSpec(row, specFilter)) return false;
      if (!q) return true;
      const hay = [row.mechanic_name, row.shop_name, row.code, row.number, row.city, row.area]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [partners, filterActive, filterLinked, filterKyc, typeFilter, specFilter, search]);

  const visibleIds = useMemo(() => new Set(visible.map((row) => String(row.id))), [visible]);

  const crewByPartner = useMemo(() => {
    const map = new Map();
    crew.forEach((row) => {
      const pid = String(row.partner_id || '');
      if (!pid) return;
      const list = map.get(pid) || [];
      list.push(row);
      map.set(pid, list);
    });
    return map;
  }, [crew]);

  const visibleStaff = useMemo(() => {
    if (!showStaff) return [];
    return crew.filter((row) => {
      if (!visibleIds.has(String(row.partner_id))) return false;
      return Boolean(parseLatLng(row.service_center_lat, row.service_center_lng));
    });
  }, [crew, showStaff, visibleIds]);

  const stats = useMemo(() => {
    const ringPartners = visible.filter((r) => (ringsByMechanic.get(String(r.id)) || []).length > 0).length;
    return {
      shown: visible.length,
      ringPartners,
      staffPins: visibleStaff.length,
    };
  }, [visible, ringsByMechanic, visibleStaff]);

  const destroyMap = () => {
    layersRef.current.clear();
    partnersLayerRef.current = null;
    staffLayerRef.current = null;
    jobsLayerRef.current = null;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rows, missing, recentJobs, rings, crewRows] = await Promise.all([
        fetchAllWithCoords(),
        countLinkedMissingGps(),
        fetchRecentJobs(),
        fetchRecentRings(),
        fetchCrewMembers(),
      ]);
      setPartners(rows);
      setMissingGps(missing);
      setJobs(recentJobs);
      setRingsByMechanic(rings);
      setCrew(crewRows);
    } catch (e) {
      setError(e.message || 'Could not load coverage');
    } finally {
      setLoading(false);
    }
  }, []);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    loadData();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      destroyMap();
    };
  }, [open, loadData]);

  useEffect(() => {
    if (!open || loading || !mapEl.current) return undefined;
    const drawId = ++drawGenRef.current;

    const draw = async () => {
      try {
        const L = await loadLeaflet();
        if (drawId !== drawGenRef.current || !mapEl.current) return;
        const createdNow = !mapRef.current;
        if (!mapRef.current) {
          mapRef.current = L.map(mapEl.current, {
            zoomControl: true,
            attributionControl: true,
            minZoom: 4,
            maxZoom: 19,
            maxBounds: [
              [6.5, 67.5],
              [37.5, 97.5],
            ],
            maxBoundsViscosity: 0.8,
          }).setView(INDIA_CENTER, 5);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap',
          }).addTo(mapRef.current);
        }

        const map = mapRef.current;
        ensurePanes(map, L);
        if (partnersLayerRef.current) {
          partnersLayerRef.current.clearLayers();
        } else {
          partnersLayerRef.current = L.layerGroup().addTo(map);
        }
        if (staffLayerRef.current) {
          staffLayerRef.current.clearLayers();
        } else {
          staffLayerRef.current = L.layerGroup().addTo(map);
        }
        if (jobsLayerRef.current) {
          jobsLayerRef.current.clearLayers();
        } else {
          jobsLayerRef.current = L.layerGroup().addTo(map);
        }
        layersRef.current.clear();

        const flyToJob = (lat, lng) => {
          const ll = parseLatLng(lat, lng);
          if (!ll || !mapRef.current) return;
          mapRef.current.flyTo(ll, 14, { duration: 0.55 });
        };

        const bounds = [];
        visible.forEach((row) => {
          const latlng = parseLatLng(row.service_center_lat, row.service_center_lng);
          if (!latlng) return;
          const rings = ringsByMechanic.get(String(row.id)) || [];
          const style = styleFor(row, rings.length);
          const group = L.layerGroup();
          const area = drawSavedArea(L, group, latlng, row, style);
          const marker = L.circleMarker(latlng, {
            pane: 'coveragePins',
            radius: rings.length > 0 ? 9 : 7,
            color: '#fff',
            weight: 2,
            fillColor: style.fill,
            fillOpacity: 1,
            interactive: true,
            bubblingMouseEvents: false,
          }).addTo(group);

          const staffList = crewByPartner.get(String(row.id)) || [];
          const staffOwn = staffList.filter((c) => parseLatLng(c.service_center_lat, c.service_center_lng));
          const staffShop = staffList.filter((c) => !parseLatLng(c.service_center_lat, c.service_center_lng));
          const place = [row.city, row.area].filter(Boolean).join(' · ') || 'City not set';
          const html = `<div style="min-width:220px;max-width:320px">
              <div style="font-weight:700;font-size:13px;margin-bottom:4px">${escapeHtml(partnerName(row))}</div>
              <div style="font-size:11px;color:#475569;margin-bottom:6px">${escapeHtml(style.label)}</div>
              <div style="font-size:12px;line-height:1.45">
                ${row.code ? `<div><b>${escapeHtml(row.code)}</b></div>` : ''}
                <div>${escapeHtml(TYPE_LABEL[row.partner_type] || 'Mechanic')}</div>
                <div>Specs: <b>${escapeHtml(normalizeSpecs(partnerSpecText(row)).join(', ') || '—')}</b></div>
                <div>KYC: <b>${String(row.kyc_status || '').toLowerCase() === 'verified' ? 'Verified' : 'Unverified'}</b></div>
                <div style="margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0">
                  <div style="font-size:10px;font-weight:800;letter-spacing:.04em;color:#64748b;text-transform:uppercase">Form location</div>
                  <div>${escapeHtml(place)}</div>
                  <div style="font-size:11px;color:#334155">Pin ${escapeHtml(fmtCoord(row.service_center_lat, row.service_center_lng))}</div>
                  <div>Saved area: <b>${area.kind === 'hexagon' ? 'Hexagon' : `Circle ${area.km} km`}</b></div>
                </div>
                ${row.number ? `<div>${escapeHtml(row.number)}</div>` : ''}
                ${
                  staffOwn.length
                    ? `<div style="margin-top:6px;color:#0e7490"><b>${staffOwn.length}</b> staff using own location</div>`
                    : ''
                }
                ${
                  staffShop.length
                    ? `<div style="color:#475569">Staff on shop area: ${escapeHtml(
                        staffShop.map(crewName).join(', '),
                      )}</div>`
                    : ''
                }
                ${ringListHtml(rings, row.id, row.service_center_lat, row.service_center_lng)}
              </div>
            </div>`;

          bindHoverPopup(marker, html, {
            onOpen: (el) => {
              el.querySelectorAll('[data-fly-lat]').forEach((a) => {
                a.addEventListener('click', (ev) => {
                  ev.preventDefault();
                  flyToJob(a.getAttribute('data-fly-lat'), a.getAttribute('data-fly-lng'));
                });
              });
            },
          });
          marker.on('click', () => setSelectedId(row.id));
          group.addTo(partnersLayerRef.current);
          layersRef.current.set(row.id, { group, marker });
          bounds.push(latlng);
        });

        visibleStaff.forEach((row) => {
          const latlng = parseLatLng(row.service_center_lat, row.service_center_lng);
          if (!latlng) return;
          const shop = partners.find((p) => String(p.id) === String(row.partner_id));
          const km = Number(row.service_radius_km) > 0 ? Number(row.service_radius_km) : DEFAULT_RADIUS_KM;
          const hex = parseHexPoints(row.service_area_hex);
          const group = L.layerGroup();
          if (hex) {
            L.polygon(hex, {
              pane: 'coverageArea',
              color: '#0e7490',
              weight: 1.2,
              fillColor: '#22d3ee',
              fillOpacity: 0.08,
              interactive: false,
            }).addTo(group);
          } else {
            L.circle(latlng, {
              pane: 'coverageArea',
              radius: km * 1000,
              color: '#0e7490',
              weight: 1,
              fillColor: '#22d3ee',
              fillOpacity: 0.08,
              interactive: false,
            }).addTo(group);
          }
          const marker = L.circleMarker(latlng, {
            pane: 'coverageStaff',
            radius: 6,
            color: '#fff',
            weight: 2,
            fillColor: '#0891b2',
            fillOpacity: 1,
            interactive: true,
            bubblingMouseEvents: false,
          }).addTo(group);
          const html = `<div style="min-width:180px">
              <div style="font-weight:700;font-size:13px">${escapeHtml(crewName(row))}</div>
              <div style="font-size:11px;color:#0e7490;margin-bottom:6px">Staff · own location (not shop pin)</div>
              <div style="font-size:12px;line-height:1.45">
                <div>Shop: <b>${escapeHtml(shop ? partnerName(shop) : 'Partner')}</b></div>
                ${row.phone ? `<div>${escapeHtml(row.phone)}</div>` : ''}
                ${row.specialization ? `<div>Spec: ${escapeHtml(row.specialization)}</div>` : ''}
                <div style="margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0">
                  <div style="font-size:10px;font-weight:800;letter-spacing:.04em;color:#64748b;text-transform:uppercase">Staff form location</div>
                  <div>Pin ${escapeHtml(fmtCoord(row.service_center_lat, row.service_center_lng))}</div>
                  <div>Saved area: <b>${hex ? 'Hexagon' : `Circle ${km} km`}</b></div>
                </div>
              </div>
            </div>`;
          bindHoverPopup(marker, html);
          marker.on('click', () => setSelectedId(row.partner_id));
          group.addTo(staffLayerRef.current);
          bounds.push(latlng);
        });

        if (showJobs && jobs.length) {
          const groups = new Map();
          jobs.forEach((job) => {
            const latlng = parseLatLng(job.pickup_latitude, job.pickup_longitude);
            if (!latlng) return;
            const key = `${latlng[0].toFixed(4)}:${latlng[1].toFixed(4)}`;
            if (!groups.has(key)) groups.set(key, { latlng, jobs: [] });
            groups.get(key).jobs.push(job);
          });
          groups.forEach((bundle) => {
            const marker = L.circleMarker(bundle.latlng, {
              pane: 'coverageJobs',
              radius: bundle.jobs.length > 1 ? 6 : 5,
              color: '#fff',
              weight: 1.5,
              fillColor: '#7c3aed',
              fillOpacity: 0.95,
              interactive: true,
              bubblingMouseEvents: false,
            });
            const list = bundle.jobs
              .slice(0, 6)
              .map((job) => {
                const when = job.created_at ? new Date(job.created_at).toLocaleString('en-IN', { hour12: true }) : '';
                return `<div style="padding:5px 0;border-top:1px solid #e2e8f0">
                  <div style="font-weight:700;font-size:11px">${escapeHtml(job.service_type || 'RSA')} · ${escapeHtml(
                    job.request_status || '',
                  )}</div>
                  <div style="font-size:11px">${escapeHtml(job.pickup_address || 'Location')}</div>
                  <div style="font-size:10px;color:#64748b">${escapeHtml(when)}</div>
                  ${
                    job.assigned_mechanic_name
                      ? `<div style="font-size:10px">Assigned: ${escapeHtml(job.assigned_mechanic_name)}</div>`
                      : ''
                  }
                </div>`;
              })
              .join('');
            const html = `<div style="min-width:180px">
                <div style="font-weight:700;font-size:12px;margin-bottom:4px">Job pickup · last 14 days</div>
                <div style="font-size:11px;color:#64748b">${bundle.jobs.length} job${
                  bundle.jobs.length === 1 ? '' : 's'
                } at this pin</div>
                ${list}
              </div>`;
            bindHoverPopup(marker, html);
            marker.addTo(jobsLayerRef.current);
          });
        }

        if (drawId !== drawGenRef.current) return;
        if (createdNow) {
          map.setView(INDIA_CENTER, 5);
        }
        setTimeout(() => {
          if (drawId !== drawGenRef.current || !mapRef.current) return;
          mapRef.current.invalidateSize();
          if (createdNow) mapRef.current.setView(INDIA_CENTER, 5);
        }, 80);
      } catch (e) {
        if (drawId === drawGenRef.current) setError(e.message || 'Map failed to load');
      }
    };

    draw();
    return () => {
      drawGenRef.current += 1;
    };
  }, [open, loading, visible, jobs, showJobs, ringsByMechanic, visibleStaff, crewByPartner, partners]);

  const focusPartner = (row) => {
    setSelectedId(row.id);
    const entry = layersRef.current.get(row.id);
    const map = mapRef.current;
    const latlng = parseLatLng(row.service_center_lat, row.service_center_lng);
    if (!map || !latlng) return;
    map.flyTo(latlng, 12, { duration: 0.6 });
    if (entry?.marker) entry.marker.openPopup();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-label="Close coverage map"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full max-h-[940px] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
              Check coverage
            </p>
            <h3 className="text-lg font-semibold text-slate-900">Where linked partners get job rings</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Hover a pin for details. Green = shop form location. Cyan = staff own area. Purple = job
              pickups. Click a ring row to fly to that job.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600 sm:block">
              <span className="font-semibold text-emerald-700">{stats.shown}</span> on map ·{' '}
              <span className="font-semibold text-indigo-700">{stats.ringPartners}</span> recent rings ·{' '}
              <span className="font-semibold text-cyan-700">{stats.staffPins}</span> staff pins ·{' '}
              <span className="font-semibold text-amber-700">{missingGps}</span> linked, no GPS
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {['all', 'active', 'inactive'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilterActive(key)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold capitalize ${
                  filterActive === key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {key}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All login' },
              { id: 'linked', label: 'Linked' },
              { id: 'unlinked', label: 'Not linked' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilterLinked(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  filterLinked === opt.id ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All KYC' },
              { id: 'verified', label: 'Verified' },
              { id: 'unverified', label: 'Unverified' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilterKyc(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  filterKyc === opt.id ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All types' },
              { id: 'mechanic', label: 'Mechanic' },
              { id: 'towing', label: 'Towing' },
              { id: 'both', label: 'Both' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTypeFilter(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  typeFilter === opt.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={showJobs}
              onChange={(e) => setShowJobs(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600"
            />
            Job pickups ({jobs.length})
          </label>
          <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={showStaff}
              onChange={(e) => setShowStaff(e.target.checked)}
              className="rounded border-slate-300 text-cyan-600"
            />
            Staff pins ({visibleStaff.length})
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Spec
          </span>
          {SPEC_FILTERS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSpecFilter(opt.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                specFilter === opt.id
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[300px] shrink-0 flex-col border-r border-slate-100 bg-slate-50/70">
            <div className="relative border-b border-slate-100 p-2.5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find partner on map…"
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-xs text-slate-500">
                  <Loader2 size={14} className="animate-spin" />
                  Loading partners…
                </div>
              ) : visible.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-slate-500">
                  No partners with GPS in this filter.
                </p>
              ) : (
                visible.map((row) => {
                  const rings = ringsByMechanic.get(String(row.id)) || [];
                  const style = styleFor(row, rings.length);
                  const kycVerified = String(row.kyc_status || '').toLowerCase() === 'verified';
                  const staffOwn = (crewByPartner.get(String(row.id)) || []).filter((c) =>
                    parseLatLng(c.service_center_lat, c.service_center_lng),
                  );
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => focusPartner(row)}
                      className={`flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-left hover:bg-white ${
                        selectedId === row.id ? 'bg-white ring-1 ring-inset ring-indigo-200' : ''
                      }`}
                    >
                      <span
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: style.fill }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-slate-900">
                          {partnerName(row)}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                          {kycVerified ? 'KYC ✓' : 'Unverified'} ·{' '}
                          {normalizeSpecs(partnerSpecText(row)).join(', ') || 'No spec'} ·{' '}
                          {[row.city, row.area].filter(Boolean).join(' · ') || 'No city'} ·{' '}
                          {Number(row.service_radius_km) || DEFAULT_RADIUS_KM} km
                          {rings.length ? ` · ${rings.length} rings` : ''}
                          {staffOwn.length ? ` · ${staffOwn.length} staff pin` : ''}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <div className="relative min-w-0 flex-1">
            {error ? (
              <div className="absolute inset-x-4 top-4 z-[500] rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            ) : null}
            <div ref={mapEl} className="h-full w-full bg-slate-100" />
            <div className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-lg border border-slate-200 bg-white/95 px-2.5 py-2 text-[10px] shadow-sm">
              <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Legend</p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Shop form location
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-indigo-500" /> Recent job rings
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-cyan-600" /> Staff own location
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-amber-500" /> Linked, inactive
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-slate-400" /> Not linked
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-violet-600" /> Job pickup (last 14d)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerCoverageMap;
